import { datadogLogs } from '@datadog/browser-logs'
import {
  InfiniteData,
  useInfiniteQuery,
  useQueryClient,
} from '@tanstack/react-query'
import AlertPanel from 'ext-ui/components/AlertPanel/AlertPanel'
import Button from 'ext-ui/components/Button/Button'
import React, {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useTranslate } from 'src/localization/Trns'
import {
  TransactionActivitiesResponse,
  TransactionActivity,
} from 'src/queries/transactionActivities/transactionActivities.models'
import { getTransactionActivities } from 'src/queries/transactionActivities/transactionActivities.network'
import {
  selectTransactionActivities,
  transactionActivitiesQueries,
} from 'src/queries/transactionActivities/transactionActivities.queries'

import * as style from './TransactionActivitiesTab.style'
import { getTransactionActivityPresentation } from './transactionActivityPresentation'
import TransactionActivityTimeline from './TransactionActivityTimeline'
import TransactionCommentComposer from './TransactionCommentComposer'

interface TransactionActivitiesTabProps {
  transactionId: string
  enabled: boolean
  commentsEnabled?: boolean
  merchantName?: string
}

const PROJECTION_READ_COUNT = 6
const PROJECTION_RETRY_DELAY_MS = 1000
const ACTIVITY_PAGE_LIMIT = 50

const getErrorStatus = (error: unknown): number | undefined => {
  if (!error || typeof error !== 'object') return undefined

  if ('status' in error && typeof error.status === 'number') {
    return error.status
  }

  if (
    'response' in error &&
    error.response &&
    typeof error.response === 'object' &&
    'status' in error.response &&
    typeof error.response.status === 'number'
  ) {
    return error.response.status
  }

  return undefined
}

const containsProjectedMessage = (
  activities: TransactionActivity[],
  messageId: string,
): boolean =>
  activities.some(
    (activity) =>
      activity.type === 'conversation.message.posted' &&
      typeof activity.payload === 'object' &&
      activity.payload !== null &&
      'messageId' in activity.payload &&
      activity.payload.messageId === messageId,
  )

const TransactionActivitiesSkeleton = (): ReactElement => {
  const translate = useTranslate()

  return (
    <div css={style.loading} role="status" aria-busy="true">
      <span css={style.visuallyHidden}>
        {translate('Loading transaction activity')}
      </span>
      {[0, 1, 2].map((row) => (
        <div key={row} css={style.skeletonRow} aria-hidden="true">
          <span css={style.skeletonAvatar} />
          <span css={style.skeletonContent}>
            <span css={style.skeletonHeading} />
            <span css={style.skeletonEvent} />
          </span>
        </div>
      ))}
    </div>
  )
}

const TransactionActivitiesTab = ({
  transactionId,
  enabled,
  commentsEnabled = false,
  merchantName,
}: TransactionActivitiesTabProps): ReactElement | null => {
  const translate = useTranslate()
  const queryClient = useQueryClient()
  const loggedUnsupportedActivityIds = useRef(new Set<string>())
  const projectionGenerationRef = useRef(0)
  const projectionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const projectionReadInFlightRef = useRef(false)
  const pendingProjectionAttemptsRef = useRef(new Map<string, number>())
  const projectionContextRef = useRef({
    isActive: false,
    transactionId: '',
  })
  const [projectionDelayed, setProjectionDelayed] = useState(false)
  const activityQuery = useMemo(
    () => transactionActivitiesQueries.list(transactionId),
    [transactionId],
  )
  const query = useInfiniteQuery({
    ...activityQuery,
    enabled: enabled && Boolean(transactionId),
    select: selectTransactionActivities,
  })
  const unsupportedSignature = useMemo(
    () =>
      JSON.stringify(
        (query.data ?? [])
          .filter(
            (activity) =>
              !getTransactionActivityPresentation(activity).supported,
          )
          .map(({ id, type, schemaVersion }) => ({
            id,
            type,
            schemaVersion,
          })),
      ),
    [query.data],
  )

  useEffect(() => {
    loggedUnsupportedActivityIds.current.clear()
  }, [transactionId])

  const cancelProjectionRefreshes = useCallback(() => {
    projectionGenerationRef.current += 1
    if (projectionTimerRef.current !== null) {
      clearTimeout(projectionTimerRef.current)
      projectionTimerRef.current = null
    }
    projectionReadInFlightRef.current = false
    pendingProjectionAttemptsRef.current.clear()
  }, [])

  useEffect(() => {
    projectionContextRef.current = {
      isActive: enabled && commentsEnabled && Boolean(transactionId),
      transactionId,
    }
    cancelProjectionRefreshes()
    setProjectionDelayed(false)

    return () => {
      projectionContextRef.current.isActive = false
      cancelProjectionRefreshes()
    }
  }, [cancelProjectionRefreshes, commentsEnabled, enabled, transactionId])

  const refreshUntilProjected = useCallback(
    (messageId: string) => {
      if (
        !projectionContextRef.current.isActive ||
        projectionContextRef.current.transactionId !== transactionId
      ) {
        return
      }

      if (!pendingProjectionAttemptsRef.current.has(messageId)) {
        pendingProjectionAttemptsRef.current.set(messageId, 0)
      }
      setProjectionDelayed(false)
      if (
        projectionReadInFlightRef.current ||
        projectionTimerRef.current !== null
      ) {
        return
      }

      const generation = projectionGenerationRef.current

      const readProjection = async (): Promise<void> => {
        let latestPage: TransactionActivitiesResponse | undefined
        const attemptedMessageIds = Array.from(
          pendingProjectionAttemptsRef.current.keys(),
        )
        projectionReadInFlightRef.current = true

        try {
          try {
            latestPage = await getTransactionActivities(transactionId, {
              limit: ACTIVITY_PAGE_LIMIT,
            })
          } catch {
            // A later bounded read can still observe the projection.
          }

          if (generation !== projectionGenerationRef.current) return

          const pageWithProjection = latestPage
          const projectedMessageIds = pageWithProjection
            ? attemptedMessageIds.filter((pendingMessageId) =>
                containsProjectedMessage(
                  pageWithProjection.items,
                  pendingMessageId,
                ),
              )
            : []
          if (pageWithProjection && projectedMessageIds.length > 0) {
            await queryClient.cancelQueries({
              queryKey: activityQuery.queryKey,
            })
            if (generation !== projectionGenerationRef.current) return

            queryClient.setQueryData<
              InfiniteData<TransactionActivitiesResponse, string | undefined>
            >(activityQuery.queryKey, () => ({
              pages: [pageWithProjection],
              pageParams: [undefined],
            }))
            projectedMessageIds.forEach((projectedMessageId) => {
              pendingProjectionAttemptsRef.current.delete(projectedMessageId)
            })
          }

          const expiredMessageIds = attemptedMessageIds.filter(
            (attemptedMessageId) => {
              const attempts =
                pendingProjectionAttemptsRef.current.get(attemptedMessageId)
              if (attempts === undefined) return false

              if (attempts + 1 === PROJECTION_READ_COUNT) {
                return true
              }

              pendingProjectionAttemptsRef.current.set(
                attemptedMessageId,
                attempts + 1,
              )
              return false
            },
          )
          expiredMessageIds.forEach((expiredMessageId) => {
            pendingProjectionAttemptsRef.current.delete(expiredMessageId)
          })
          if (expiredMessageIds.length > 0) {
            setProjectionDelayed(true)
          }

          if (pendingProjectionAttemptsRef.current.size === 0) {
            if (expiredMessageIds.length === 0) {
              setProjectionDelayed(false)
            }
            return
          }

          projectionTimerRef.current = setTimeout(() => {
            projectionTimerRef.current = null
            void readProjection()
          }, PROJECTION_RETRY_DELAY_MS)
        } finally {
          if (generation === projectionGenerationRef.current) {
            projectionReadInFlightRef.current = false
          }
        }
      }

      void readProjection()
    },
    [activityQuery.queryKey, queryClient, transactionId],
  )

  useEffect(() => {
    if (!enabled) return

    const unsupportedActivities = JSON.parse(unsupportedSignature) as Array<{
      id: string
      type: string
      schemaVersion: number
    }>
    unsupportedActivities.forEach(({ id, type, schemaVersion }) => {
      if (loggedUnsupportedActivityIds.current.has(id)) return

      loggedUnsupportedActivityIds.current.add(id)
      datadogLogs.logger.warn('transaction_activity.unsupported_contract', {
        errorId: 'transaction_activity.unsupported_contract',
        activityId: id,
        type,
        schemaVersion,
      })
    })
  }, [enabled, transactionId, unsupportedSignature])

  if (!enabled) return null

  const commentComposer = commentsEnabled ? (
    <>
      <div css={style.composer}>
        <TransactionCommentComposer
          transactionId={transactionId}
          onPosted={refreshUntilProjected}
        />
      </div>
      {projectionDelayed && (
        <p css={style.projectionStatus} aria-live="polite">
          {translate(
            'Comment posted. It may take a moment to appear in activity.',
          )}
        </p>
      )}
    </>
  ) : null

  if (query.isPending) {
    return (
      <div css={style.container}>
        {commentComposer}
        <TransactionActivitiesSkeleton />
      </div>
    )
  }

  const status = getErrorStatus(query.error)

  if (
    query.isError &&
    (status === 400 || status === 404 || !query.data?.length)
  ) {
    const unavailable = status === 404
    const retryable = status !== 400 && !unavailable

    return (
      <div css={style.container}>
        {commentComposer}
        <AlertPanel role="alert" variant="severe-wcag">
          <div css={style.error}>
            <p css={style.errorTitle}>
              {translate(
                unavailable
                  ? 'Transaction activity is unavailable.'
                  : 'Transaction activity could not be loaded.',
              )}
            </p>
            {retryable && (
              <div css={style.actions}>
                <Button variant="secondary" onClick={() => query.refetch()}>
                  {translate('Retry')}
                </Button>
              </div>
            )}
          </div>
        </AlertPanel>
      </div>
    )
  }

  if (!query.data?.length) {
    return (
      <div css={style.container}>
        {commentComposer}
        <div css={style.empty}>{translate('No activity yet')}</div>
      </div>
    )
  }

  return (
    <div css={style.container}>
      {commentComposer}
      <TransactionActivityTimeline
        activities={query.data}
        merchantName={merchantName}
      />
      {query.isFetchNextPageError && (
        <AlertPanel role="alert" variant="severe-wcag">
          <div css={style.error}>
            <p css={style.errorTitle}>
              {translate('Could not load more activity.')}
            </p>
            <div css={style.actions}>
              <Button variant="secondary" onClick={() => query.fetchNextPage()}>
                {translate('Retry')}
              </Button>
            </div>
          </div>
        </AlertPanel>
      )}
      {query.hasNextPage && !query.isFetchNextPageError && (
        <div css={style.loadMore}>
          <Button
            loading={query.isFetchingNextPage}
            variant="secondary"
            onClick={() => query.fetchNextPage()}
          >
            {translate('Load more')}
          </Button>
        </div>
      )}
    </div>
  )
}

export default TransactionActivitiesTab
